/**
 * Drawer panel that links an account card to a Canary organization.
 *
 * Reuses `useOrganizationsQuery` (superadmin-scoped) for the org list; the
 * accounts board is an internal CRM surface, so the picker is only useful
 * to operators who already have superadmin reach.
 */
import React from 'react';
import {
  Button,
  ComboBox,
  Flex,
  Heading,
  Text,
  TagBadge,
  useToast,
  type ComboBoxOption,
} from '@chatsdet/ui';
import { Buildings, X } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useOrganizationsQuery } from '../../../ui/pages/superadmin/useSuperadminData';
import { useSetCardLinkedOrg, useUnlinkCardOrg, type Card } from '../../cards/hooks/useCards';

interface Props {
  card: Card;
}

export function LinkOrgPicker({ card }: Props) {
  const { user } = useAuth();
  const isSuperadmin = user?.isSuperadmin === true;
  const { toast } = useToast();

  const [isEditing, setIsEditing] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const orgSearch = inputValue.trim();

  const orgsQuery = useOrganizationsQuery(
    {
      search: orgSearch || undefined,
      pageSize: 20,
      sortBy: 'name',
      sortOrder: 'asc',
    },
    { enabled: isSuperadmin }
  );
  const setLinkedOrg = useSetCardLinkedOrg('account', card.id);
  const unlink = useUnlinkCardOrg('account', card.id);

  const orgOptions: ComboBoxOption[] = React.useMemo(() => {
    if (!isSuperadmin || !orgsQuery.data?.organizations) return [];
    return orgsQuery.data.organizations.map((org) => ({
      id: org.id,
      label: org.name,
      textValue: org.name,
    }));
  }, [isSuperadmin, orgsQuery.data?.organizations]);

  const handleSelect = (key: string | null) => {
    if (!key) return;
    const org = orgOptions.find((o) => o.id === key);
    if (!org) return;
    setLinkedOrg.mutate(key, {
      onSuccess: () => {
        toast.success(`Linked to ${org.label}`);
        setIsEditing(false);
        setInputValue('');
      },
      onError: () => toast.error('Failed to link organization'),
    });
  };

  const handleUnlink = () => {
    unlink.mutate(undefined, {
      onSuccess: () => toast.success('Unlinked'),
      onError: () => toast.error('Failed to unlink'),
    });
  };

  return (
    <Flex direction="column" gap="sm" className="mt-2">
      <Heading level={5} className="text-sm">
        Linked organization
      </Heading>

      {card.linkedOrg && !isEditing ? (
        <Flex gap="sm" align="center" wrap>
          <TagBadge
            name={card.linkedOrg.name}
            color="blue"
            hideDot
            icon={<Buildings size={12} />}
          />
          <Button variant="ghost" size="sm" onPress={() => setIsEditing(true)}>
            Change
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<X size={12} />}
            onPress={handleUnlink}
            isDisabled={unlink.isPending}
          >
            Unlink
          </Button>
        </Flex>
      ) : isEditing || !card.linkedOrg ? (
        isSuperadmin ? (
          <Flex gap="xs" align="center" wrap>
            <ComboBox
              items={orgOptions}
              placeholder="Search organizations…"
              inputValue={inputValue}
              onInputChange={setInputValue}
              onSelectionChange={(key) => handleSelect(key as string | null)}
              className="w-[260px]"
              menuTrigger="focus"
              isLoading={orgsQuery.isFetching}
              emptyState={
                orgsQuery.isError
                  ? 'Failed to load organizations'
                  : orgSearch
                    ? `No organizations match "${orgSearch}"`
                    : 'No organizations found'
              }
              defaultFilter={() => true}
            />
            {isEditing && (
              <Button
                variant="ghost"
                size="sm"
                onPress={() => {
                  setIsEditing(false);
                  setInputValue('');
                }}
              >
                Cancel
              </Button>
            )}
          </Flex>
        ) : (
          <Text size="xs" className="text-muted-foreground">
            Only superadmins can link accounts to organizations.
          </Text>
        )
      ) : null}
    </Flex>
  );
}
